import React from "react";
import { Grid, Box, Typography, Tooltip } from "@mui/material";
import CustomTextField from "./common/CustomTextField";
import CustomRadio from "./CustomRadio";
import CustomCheckbox from "./CustomCheckbox";
import { COLORS } from "../assets/theme";

type DialogDataType = {
  contentContains?: string;
  replaceValue?: string;
  replaceWithValue?: string;
  operationType?: "REPLACE" | "PARSE_DIGITS" | "FORMAT_DIGITS";
  [key: string]: any;
};

type CheckboxType = {
  label: string;
  value: string;
  defaultValue?: string;
};

type CombinedSectionProps = {
  selectedRadio: string | null;
  onRadioChange: (type: string) => void;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setDialogData: React.Dispatch<React.SetStateAction<DialogDataType>>;
  dialogData: DialogDataType;
  checkboxes: CheckboxType[];
};
const CombinedSection: React.FC<CombinedSectionProps> = ({
  selectedRadio,
  onRadioChange,
  onInputChange,
  setDialogData,
  dialogData,
  checkboxes,
}) => {
  const handleRadioChange = (type: string) => {
    setDialogData((prevData) => {
      const resetFields = {
        contentContains: "",
      };

      const clearedCheckboxFields =
        type === "contentReplace"
          ? {}
          : Object.keys(prevData)
              .filter((key) => key.endsWith("Enabled"))
              .reduce((acc, key) => {
                const fieldName = key.replace("Enabled", "");
                return {
                  ...acc,
                  [key]: false,
                  [fieldName]: "",
                };
              }, {});

      if (type === "contentReplace") {
        return {
          ...prevData,
          operationType: "REPLACE",
          ...resetFields,
          ...clearedCheckboxFields,
        };
      }
      if (type === "formatDigits") {
        return {
          ...prevData,
          operationType: "FORMAT_DIGITS",
          ...resetFields,
          ...clearedCheckboxFields,
        };
      }
      return {
        ...prevData,
        operationType: "PARSE_DIGITS",
        ...resetFields,
        ...clearedCheckboxFields,
      };
    });

    onRadioChange(type);
  };

  // A space is a valid digit separator, so it counts as a value there
  const allowsWhitespaceOnly = (field: string) => field === "separateDigitsBy";

  const hasFieldValue = (field: string, fieldValue: unknown) => {
    if (fieldValue === undefined || fieldValue === null) return false;
    const text = fieldValue.toString();
    return allowsWhitespaceOnly(field) ? text !== "" : text.trim() !== "";
  };

  return (
    <Grid container mt={2}>
      {/* REPLACE Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          marginBottom: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <CustomRadio
          checked={dialogData?.operationType === "REPLACE"}
          onChange={() => handleRadioChange("contentReplace")}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              mr: { xs: 0, sm: 2 },
              mb: { xs: 1, sm: 0 },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "13px", sm: "14px" },
                lineHeight: "16.94px",
                fontWeight: 400,
                color:
                  dialogData?.operationType === "REPLACE"
                    ? "#1C1C1C"
                    : "#9e9e9e",
                textAlign: "left",
                pl: { xs: 0, sm: 3 },
              }}
            >
              If content contains (Replace)
            </Typography>
          </Box>

          <Box sx={{ ml: { xs: 0, sm: "auto" }, textAlign: "right", width: { xs: "100%", sm: "280px" } }}>
            <Tooltip title="Comma separated values (e.g. value1,value2)" placement="top" arrow>
              <span>
                <CustomTextField
                  name="contentContains"
                  placeholder="Content"
                  bgColor={COLORS.WhiteBackground}
                  value={dialogData.contentContains || ""}
                  onChange={(e) => {
                    const { value } = e.target;
                    setDialogData((prevData) => ({
                      ...prevData,
                      contentContains: value,
                    }));
                  }}
                  disabled={dialogData?.operationType !== "REPLACE"}
                  sx={{ width: { xs: "100%", sm: "280px" } }}
                />
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* Replace/With Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          marginBottom: 2,
          pl: { xs: 0, sm: 7 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            gap: 2,
            mb: { xs: 1, sm: 0 },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "14px" },
              lineHeight: "16.94px",
              fontWeight: 400,
              color:
                dialogData?.operationType === "REPLACE" ? "#1C1C1C" : "#9e9e9e",
            }}
          >
            Replace
          </Typography>
          <CustomTextField
            name="replaceValue"
            placeholder="Replace"
            minWidth="130px"
            bgColor={COLORS.WhiteBackground}
            value={dialogData.replaceValue || ""}
            onChange={onInputChange}
            disabled={dialogData?.operationType !== "REPLACE"}
            fullWidth
            sx={{ width: { xs: "100%", sm: "auto" } }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            gap: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "14px" },
              lineHeight: "16.94px",
              fontWeight: 400,
              color:
                dialogData?.operationType === "REPLACE" ? "#1C1C1C" : "#9e9e9e",
            }}
          >
            With
          </Typography>
          <CustomTextField
            name="replaceWithValue"
            placeholder="With"
            minWidth="130px"
            bgColor={COLORS.WhiteBackground}
            value={dialogData.replaceWithValue || ""}
            onChange={onInputChange}
            disabled={dialogData?.operationType !== "REPLACE"}
            fullWidth
            sx={{ width: { xs: "100%", sm: "auto" } }}
          />
        </Box>
      </Box>

      {/* PARSE_DIGITS Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          marginBottom: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <CustomRadio
          checked={dialogData?.operationType === "PARSE_DIGITS"}
          onChange={() => handleRadioChange("contentContains")}
        />
        <Box
          sx={{
            display: "flex",
            flex: 1,
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
            gap: 3,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "14px" },
              lineHeight: "16.94px",
              fontWeight: 400,
              color:
                dialogData?.operationType === "PARSE_DIGITS"
                  ? "#1C1C1C"
                  : "#9e9e9e",
              width: { xs: "100%", sm: "220px" },
              textAlign: { xs: "left", sm: "right" },
              mb: { xs: 1, sm: 0 },
            }}
          >
            Extract code if content contains
          </Typography>
          <Box sx={{ textAlign: "right", width: { xs: "100%", sm: "280px" } }}>
            <Tooltip title="Comma separated values (e.g. value1,value2)" placement="top" arrow>
              <span>
                <CustomTextField
                  name="contentContains"
                  placeholder="Content"
                  bgColor={COLORS.WhiteBackground}
                  value={dialogData.contentContains || ""}
                  onChange={(e) => {
                    const { value } = e.target;
                    setDialogData((prevData) => ({
                      ...prevData,
                      contentContains: value,
                    }));
                  }}
                  disabled={dialogData?.operationType !== "PARSE_DIGITS"}
                  sx={{ width: { xs: "100%", sm: "280px" } }}
                />
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      {/* FORMAT_DIGITS Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          marginBottom: 2,
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <CustomRadio
          checked={dialogData?.operationType === "FORMAT_DIGITS"}
          onChange={() => handleRadioChange("formatDigits")}
        />
        <Typography
          sx={{
            fontSize: { xs: "13px", sm: "14px" },
            lineHeight: "16.94px",
            fontWeight: 400,
            color:
              dialogData?.operationType === "FORMAT_DIGITS"
                ? "#1C1C1C"
                : "#9e9e9e",
            pl: { xs: 0, sm: 3 },
          }}
        >
          If content is digits ONLY
        </Typography>
      </Box>

      {/* Checkbox Section */}
      {checkboxes
        .filter(({ value }) =>
          dialogData?.operationType === "FORMAT_DIGITS"
            ? value !== "isAlphanumeric" && value !== "codeLength"
            : true
        )
        .map(({ label, value, defaultValue }) => (
        <Box
          key={value}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2, flexDirection: { xs: "column", sm: "row" }, gap: { xs: 1, sm: 0 } }}
        >
          <Box sx={{ flexShrink: 0, mr: { xs: 0, sm: 1 }, mb: { xs: 1, sm: 0 } }}>
            <CustomCheckbox
              checked={
                value === "isAlphanumeric"
                  ? !!dialogData[value]
                  : !!dialogData[`${value}Enabled`] || hasFieldValue(value, dialogData[value])
              }
              onChange={(e) => {
                const { checked } = e.target;
                if (value === "isAlphanumeric") {
                  setDialogData((prevData) => ({
                    ...prevData,
                    [value]: checked,
                  }));
                } else {
                  setDialogData((prevData) => ({
                    ...prevData,
                    [`${value}Enabled`]: checked,
                    [value]: checked
                      ? prevData[value] || defaultValue || ""
                      : "",
                  }));
                }
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              flex: 1,
              alignItems: "center",
              gap: 3,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "13px", sm: "14px" },
                lineHeight: "16.94px",
                fontWeight: 400,
                color:
                  value === "isAlphanumeric"
                    ? dialogData[value]
                      ? "#1C1C1C"
                      : "#9e9e9e"
                    : dialogData[`${value}Enabled`] || hasFieldValue(value, dialogData[value])
                    ? "#1C1C1C"
                    : "#9e9e9e",
                width: { xs: "100%", sm: "200px" },
                textAlign: "left",
                mb: { xs: 1, sm: 0 },
              }}
            >
              {label}
            </Typography>
            {value !== "isAlphanumeric" && (
              <Box sx={{ flex: 1, textAlign: "right", width: { xs: "100%", sm: "280px" } }}>
                <CustomTextField
                  name={value}
                  placeholder={`Enter ${label.toLowerCase()}`}
                  value={dialogData[value] || ""}
                  onChange={(e) => {
                    let { value: textValue } = e.target;
                    // If it's codeLength field, only allow numbers
                    if (value === "codeLength") {
                      textValue = textValue.replace(/[^0-9]/g, "");
                    }
                    setDialogData((prevData) => ({
                      ...prevData,
                      [value]: textValue,
                      [`${value}Enabled`]: value === "codeLength"
                        ? prevData[`${value}Enabled`]
                        : hasFieldValue(value, textValue),
                    }));
                  }}
                  disabled={!dialogData[`${value}Enabled`] && !hasFieldValue(value, dialogData[value])}
                  allowWhitespaceOnly={allowsWhitespaceOnly(value)}
                  bgColor={COLORS.WhiteBackground}
                  sx={{ width: { xs: "100%", sm: "280px" } }}
                  inputProps={value === "codeLength" ? {
                    inputMode: "numeric",
                    pattern: "[0-9]*"
                  } : undefined}
                />
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Grid>
  );
};

export default CombinedSection;
